import { useEffect, useRef, useState } from 'react';
import { updateRecordResponse } from '@/features/datasets/api/exampleUpdates';
import { fromEditorEntities } from '@/features/datasets/utils/nerAnnotationEntities';
import { ActivityType } from '@/shared/domain/taskTypes';

// Same cadence as the Model Configuration prompt autosave.
export const NER_ANNOTATION_AUTOSAVE_DEBOUNCE_MS = 700;

/**
 * Debounced autosave for the editable NER annotate modal.
 *
 * The caller owns the draft (`{ text, entities }` in editor format) and pushes
 * every change through `commitChange(nextDraft, activity, immediate)`:
 * - typing → debounced save (700ms, like the prompt autosave),
 * - discrete entity actions (tag / untag / validate / remove) → immediate save
 *   with the matching `ActivityType`, mirroring the legacy tagger.
 *
 * Saves are serialized (never more than one in flight), keyed by `record._id`
 * plus the last text the server is known to have, and include
 * `new_request_example` when the example text changed so the backend updates
 * text + entities atomically. A pending debounce is flushed on unmount (modal
 * close). Failed saves keep the draft dirty so the next change retries.
 */
export default function useNerAnnotationAutoSave({
    record,
    datasetName,
    isBenchmark,
    llm_usecase_id,
    taskType,
    updateRecord,
    fetchValidationCounts,
    onDataOverviewRefresh,
    enabled = true,
    initialDraft,
}) {
    const serialize = (draft) => JSON.stringify(fromEditorEntities(draft?.text, draft?.entities));

    const timerRef = useRef(null);
    const inFlightRef = useRef(false);
    const latestRef = useRef(initialDraft);
    // Baseline is the converted initial draft (not the raw record), so entities
    // dropped defensively by the converter don't trigger a save on open.
    const lastSavedRef = useRef({
        text: initialDraft?.text || '',
        entitiesJson: serialize(initialDraft),
    });
    const pendingActivityRef = useRef(null);
    const mountedRef = useRef(true);
    const [autosaving, setAutosaving] = useState(false);

    const paramsRef = useRef({});
    paramsRef.current = {
        record,
        datasetName,
        isBenchmark,
        llm_usecase_id,
        taskType,
        updateRecord,
        fetchValidationCounts,
        onDataOverviewRefresh,
        enabled,
    };

    // Stable API: every function below only reads refs, so the first-render
    // closures stay valid for the lifetime of the hook.
    const apiRef = useRef(null);
    if (!apiRef.current) {
        const isDirty = () => {
            const latest = latestRef.current;
            if (!latest) return false;
            if ((latest.text || '') !== lastSavedRef.current.text) return true;
            return serialize(latest) !== lastSavedRef.current.entitiesJson;
        };

        const clearTimer = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };

        const schedule = (delay) => {
            clearTimer();
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                persistLatest();
            }, delay);
        };

        async function persistLatest() {
            const params = paramsRef.current;
            if (!params.enabled || inFlightRef.current || !isDirty()) return;
            const latest = latestRef.current;
            const text = latest.text || '';
            // Never persist an empty example — the record must keep its text.
            // The draft stays dirty, so restoring text resumes autosave.
            if (!text.trim()) return;
            const wireEntities = fromEditorEntities(text, latest.entities);
            const entitiesJson = JSON.stringify(wireEntities);
            const activity = pendingActivityRef.current;
            pendingActivityRef.current = null;
            const previousText = lastSavedRef.current.text;
            const newResponse = { ...(params.record?.response || {}), entities: wireEntities };
            inFlightRef.current = true;
            if (mountedRef.current) setAutosaving(true);
            try {
                await updateRecordResponse(
                    params.record,
                    newResponse,
                    params.datasetName,
                    params.isBenchmark,
                    params.updateRecord,
                    params.fetchValidationCounts,
                    params.llm_usecase_id,
                    activity?.type ?? ActivityType.example_text_edited,
                    activity?.comment ?? null,
                    params.taskType,
                    params.onDataOverviewRefresh,
                    {
                        recordId: params.record?._id,
                        requestExample: previousText,
                        newRequestExample: text !== previousText ? text : null,
                    }
                );
                lastSavedRef.current = { text, entitiesJson };
            } catch {
                // updateRecordResponse already showed a toast; keep the draft
                // dirty so the next change (or flush) retries.
            } finally {
                inFlightRef.current = false;
                if (mountedRef.current) setAutosaving(false);
                // Edits made while the request was in flight → save again.
                if (isDirty()) schedule(NER_ANNOTATION_AUTOSAVE_DEBOUNCE_MS);
            }
        }

        const commitChange = (nextDraft, activity = null, immediate = false) => {
            latestRef.current = nextDraft;
            if (activity) pendingActivityRef.current = activity;
            if (!paramsRef.current.enabled) return;
            if (immediate) {
                clearTimer();
                persistLatest();
                return;
            }
            schedule(NER_ANNOTATION_AUTOSAVE_DEBOUNCE_MS);
        };

        const flushSave = () => {
            clearTimer();
            return persistLatest();
        };

        // Adopt a fresh server record as the new baseline (e.g. after a tag
        // category was deleted server-side), discarding any pending activity.
        const resetBaseline = (draft) => {
            clearTimer();
            latestRef.current = draft;
            lastSavedRef.current = { text: draft?.text || '', entitiesJson: serialize(draft) };
            pendingActivityRef.current = null;
        };

        apiRef.current = { commitChange, flushSave, resetBaseline };
    }

    // Flush a pending debounce when the modal body unmounts (modal closed).
    useEffect(
        () => () => {
            mountedRef.current = false;
            if (timerRef.current) {
                apiRef.current.flushSave();
            }
        },
        []
    );

    return { ...apiRef.current, autosaving };
}
